"use client";

import { useRouter } from "@/i18n/navigation";
import type {
    ClientToServerEvents,
    GameInviteData,
    ServerToClientEvents,
} from "@satranc365/shared";
import { useState } from "react";
import type { Socket } from "socket.io-client";
import { toast } from "sonner";
import { InviteCard } from "./app-ui/invite-card";

interface GameInviteItemProps {
    invite: GameInviteData;
    socket: Socket<ServerToClientEvents, ClientToServerEvents> | null;
    onRemove: (inviteId: number) => void;
}

function formatTimeControl(initial: number, increment: number): string {
    const minutes = Math.floor(initial / 60000);
    const seconds = Math.floor(increment / 1000);
    return `${minutes} + ${seconds}`;
}

const GameInviteItem = ({ invite, socket, onRemove }: GameInviteItemProps) => {
    const router = useRouter();
    const [isResponding, setIsResponding] = useState(false);

    const handleAccept = () => {
        if (!socket || isResponding) return;

        setIsResponding(true);
        socket.emit(
            "respond_to_game_invite",
            invite.inviteId,
            true,
            (response) => {
                setIsResponding(false);
                if (response.success) {
                    onRemove(invite.inviteId);
                    router.push(`/oyun/${invite.roomCode}?fromInvite=true`);
                } else {
                    toast.error(response.error || "Davet kabul edilemedi");
                    onRemove(invite.inviteId);
                }
            },
        );
    };

    const handleDecline = () => {
        if (!socket || isResponding) return;

        setIsResponding(true);
        socket.emit(
            "respond_to_game_invite",
            invite.inviteId,
            false,
            (response) => {
                setIsResponding(false);
                onRemove(invite.inviteId);
                if (!response.success) {
                    toast.error(response.error || "Davet reddedilemedi");
                }
            },
        );
    };

    return (
        <InviteCard
            from={{
                nickname: invite.fromDisplayUsername,
                userId: invite.fromUserId,
                isPremium: invite.fromIsPremium,
            }}
            title="sizi oyuna davet etti"
            subtitle={formatTimeControl(
                invite.timeControl.initial,
                invite.timeControl.increment,
            )}
            onAccept={handleAccept}
            onDecline={handleDecline}
            isLoading={isResponding}
        />
    );
};

export default GameInviteItem;
